import React, { useEffect, useRef, useState, useCallback } from "react";
import { Card, Button, Spinner, Col, Row, Form } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  generateTaskHtml,
  filterSubtasksMap,
  handleSendProposal,
  fetchTemplateSections,
  getTranslatedSalutation
} from "../../../assets/js/common";
import axios from "axios";
import "assets/css/template.css";
import "@fortawesome/fontawesome-free/css/all.min.css"; // Import Font Awesome
import EditableSectionModal from "../proposalModals/pditableSectionModal";
import IconButton from "components/common/iconButton";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { handleUploadPDF, fitImageToBox } from "components/proposals/proposalPdfMethods/handleProposalPdf";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import SignatureModal from "components/profile/signatureModal"; // adjust path if needed
import PDFVierwer from "../pdfVierwer";

const TemplateViewerClient = ({ formData, workType, logoSrc }) => {
  const { t } = useTranslation();
  const [templateConfig, setTemplateConfig] = useState(null);
  const [sectionMap, setSectionMap] = useState({});
  const [values, setValues] = useState({});
  const [proposalTaskData, setProposalTaskData] = useState([]);
  const [task, setTask] = useState([]); // assume you'll fetch this
  const [subtasksMap, setSubtasksMap] = useState({});
  const [templateTaskContent, setTemplateTaskContent] = useState("");
  const invoiceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // --- Asperion token state ---
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [asperionUserId, setAsperionUserId] = useState(null);
  // ----------------------------

  const signatureRefClient = useRef(null);
  const [clientSignature, setClientSignature] = useState("");
  const [isClientSignatureBtnDisabled, setIsClientSignatureBtnDisabled] = useState(false);
  const isImportedProposal = formData?.proposal?.IsImported === true;
  const importedPdfUrl = formData?.proposalDetailsData?.Document;

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const showGlobalLoader = !isImportedProposal && isLoading;
  const pdfFetchStartedRef = useRef(false);
  const [importedPdfBlob, setImportedPdfBlob] = useState(null);
  const isDownloadDisabled = isImportedProposal
    ? pdfLoading || !importedPdfBlob
    : false;

  // Company Signature Image with Date
  const companySignatureImage = formData?.proposalDetailsData?.CompanySignature
    ? `data:image/png;base64,${formData?.proposalDetailsData?.CompanySignature}`
    : null;
  const companySignatureDate = formData?.proposalDetailsData
    ?.CompanySignatureDate
    ? moment(formData?.proposalDetailsData?.CompanySignatureDate).format(
      "DD-MM-YYYY"
    )
    : null;

  const [justApproved, setJustApproved] = useState(false);
  const isApproved = formData?.proposal.Status === "1" || justApproved;

  const proposalDocumentDetail = formData?.proposalDetailsData || {};
  const relationData = formData?.relationData || {};
  const [comment, setComment] = useState("");
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [tempClientSignature, setTempClientSignature] = useState(null);

  const [locationConfig, setLocationConfig] = useState(null);


  const fetchPdfBlob = async (relationId, fileName) => {
    const response =
      await axios.get(
        `${process.env.REACT_APP_API_URL}/blobRoute/getBlob`,
        {
          params: {
            blobUrl
          },
          responseType: "blob",
          headers: {
            Authorization:
              process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

    return response.data;
  };

  useEffect(() => {
    const loadImportedPdf = async () => {
      if (
        !isImportedProposal ||
        !importedPdfUrl ||
        pdfFetchStartedRef.current
      ) {
        return;
      }

      pdfFetchStartedRef.current = true; // 🔒 HARD LOCK

      try {
        setPdfLoading(true);

        const blob =
          await fetchPdfBlob(
            importedPdfUrl
          );

        setImportedPdfBlob(blob);
        setPdfUrl(URL.createObjectURL(blob));
      } catch (err) {
        console.error("Error loading imported PDF (client):", err);
        pdfFetchStartedRef.current = false; // allow retry if failed
      } finally {
        setPdfLoading(false);
      }
    };

    loadImportedPdf();
  }, [isImportedProposal, importedPdfUrl]);

  useEffect(() => {
    if (formData?.proposal?.ClientComment) {
      setComment(formData.proposal.ClientComment);
    }
  }, [formData]);

  useEffect(() => {
    const preloaded = formData?.preloadedTasks;
    if (Array.isArray(preloaded) && preloaded.length > 0) {
      setProposalTaskData(preloaded);
      setSubtasksMap(formData?.preloadedAllSubtasksByTaskId || {});
      return;
    }

    fetchProposalTask(formData?.proposal?.ProposalId);
  }, [formData?.proposal?.ProposalId, formData?.preloadedTasks]);

  useEffect(() => {
    if (formData?.proposal?.CustomerId && workType) {
      fetchTemplateSections(
        formData.proposal.CustomerId,
        formData?.proposal?.TemplateId,
        workType,
        setTemplateConfig,
        setSectionMap,
        setValues,
        formData?.proposalDetailsData,
        formData?.proposal?.ProposalId,
        formData?.proposal?.CurrentVersionId,
      );
    }
  }, [formData?.proposal?.CustomerId, formData?.proposal?.TemplateId, workType, formData?.proposal?.ProposalId, formData?.proposalDetailsData]);

  // --- Fetch Asperion token for the proposal's location on mount ---
  useEffect(() => {
    const fetchAsperionTokenForLocation = async () => {
      try {
        const locationId = formData?.proposal?.LocationId;
        if (!locationId) return;

        const locationResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/locationRoute/getById/${locationId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        const locationData = locationResponse?.data?.data || locationResponse?.data;
        const autoSyncFlag = locationData?.AutoAsperionSyncFlag;
        const isAutoSyncEnabled =
          autoSyncFlag === true || autoSyncFlag === 1 || autoSyncFlag === "1";

        setLocationConfig({ locationData, isAutoSyncEnabled });

        if (!isAutoSyncEnabled) return;

        const asperionId = locationData?.AsperionUserId;
        if (!asperionId) return;
        setAsperionUserId(asperionId);

        const tokenResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/asperionAutoTokenRoute/getToken/${asperionId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        const tokens = tokenResponse.data;
        if (!tokens) return;

        const expiry = new Date(tokens.TokenExpiresAt).getTime();
        const isExpired = expiry < Date.now();

        if (isExpired) {
          const refreshed = await refreshAsperionToken(tokens.RefreshToken, asperionId);
          setAccessToken(refreshed.AccessToken);
          setRefreshToken(refreshed.RefreshToken);
        } else {
          setAccessToken(tokens.AccessToken);
          setRefreshToken(tokens.RefreshToken);
        }
      } catch (err) {
        console.error("Failed to fetch Asperion token for location:", err);
      }
    };

    fetchAsperionTokenForLocation();
  }, [formData?.proposal?.LocationId]);
  // ----------------------------------------------------------------

  const fetchProposalTask = async (proposalId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setProposalTaskData(response.data);
    } catch (error) {
      console.error("Error fetching proposal task data:", error);
    }
  };

  useEffect(() => {
    if (!proposalTaskData || proposalTaskData.length === 0) return;

    fetchTask();

    if (Array.isArray(formData?.preloadedTasks) && formData.preloadedTasks.length > 0) {
      return;
    }

    const fetchAllSubtasks = async () => {
      const results = await Promise.all(
        proposalTaskData.map(async (task) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
            );
            return [task.TaskId, response.data];
          } catch (error) {
            console.error(
              `Error fetching subtasks for task ${task.TaskId}`,
              error
            );
            return null;
          }
        })
      );

      const allSubtasks = {};
      results.filter(Boolean).forEach(([taskId, subtasks]) => {
        allSubtasks[taskId] = subtasks;
      });
      setSubtasksMap(allSubtasks);
    };

    fetchAllSubtasks();
  }, [proposalTaskData, formData?.preloadedTasks]);

  const fetchTask = async () => {
    const customerId = formData?.proposal.CustomerId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/taskRoute/tasks`,
        {
          params: { customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setTask(response.data);
    } catch (error) {
      console.error("Error fetching task data:", error);
    }
  };

  useEffect(() => {
    if (proposalTaskData && proposalTaskData.length > 0 && task.length > 0) {
      const selectedSubtasks = Array.isArray(formData.selectedSubtasks)
        ? formData.selectedSubtasks
        : [];

      const filteredMap = filterSubtasksMap(subtasksMap, selectedSubtasks);

      const tasksHtml = generateTaskHtml(
        proposalTaskData,
        task,
        filteredMap,
        selectedSubtasks,
        t,
        formData?.proposal?.TemplateName
      );

      setTemplateTaskContent(tasksHtml);
    }
  }, [
    proposalTaskData,
    task,
    subtasksMap,
    formData.selectedSubtasks,
    t,
    workType,
    formData?.proposal?.TemplateName
  ]);

  useEffect(() => {
    const clientSig = formData?.proposalDetailsData?.ClientSignature;
    if (!clientSig) return;

    setClientSignature(clientSig);
    setIsClientSignatureBtnDisabled(true);

    if (isImportedProposal) return;

    if (!isApproved) {
      loadAndDrawSignature(clientSig, signatureRefClient);
    }
  }, [
    formData?.proposalDetailsData?.ClientSignature,
    isApproved,
    isImportedProposal
  ]);


  const renderSection = (key) => {
    const section = sectionMap[key];
    if (!section) return null;

    if (section.type === "dynamic") {
      switch (section.source) {
        case "introTextBlock":
          const introText = t(`introTextBlock.introText`, {
            StreetName: formData?.relationData?.StreetName || "",
            PostCode: formData?.relationData?.PostCode || "",
            City: formData?.relationData?.City || "",
          });

          return (
            <p key={key} className="fs-10 mb-3">
              {introText}
            </p>
          );

        case "timeScheduleText": {
          const { startDate, endDate, startTime } = formData || {};
          if (!startDate) return null;

          const formattedStartDate = moment(startDate).format("DD-MM-YYYY");
          const formattedStartTime = startTime
            ? moment(startTime).format("HH:mm")
            : "";
          const formattedEndDate = endDate
            ? moment(endDate).format("DD-MM-YYYY")
            : "";

          let timeText = "";

          if (startTime && endDate) {
            timeText = t("timeScheduleTextWithEnd", {
              StartDate: formattedStartDate,
              StartTime: formattedStartTime,
              EndDate: formattedEndDate,
            });
          } else if (startTime) {
            timeText = t("timeScheduleTextWithoutEnd", {
              StartDate: formattedStartDate,
              StartTime: formattedStartTime,
            });
          } else {
            timeText = t("timeScheduleTextOnlyDate", {
              StartDate: formattedStartDate,
            });
          }

          return (
            <p key={key} className="fs-10 mb-3">
              {timeText}
            </p>
          );
        }

        case "proposalTasksBlock":
          if (!templateTaskContent?.trim()) return null;
          return (
            <div className="fs-10 mb-3" key={key}>
              <span dangerouslySetInnerHTML={{ __html: templateTaskContent }} />
            </div>
          );

        case "pageBreakAfterExtraContent":
          return (
            <div
              className="text-muted text-center"
              style={{
                margin: "40px 0",
                borderTop: "2px dashed #aaa",
                position: "relative",
                fontSize: "12px",
                color: "#777",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "-10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#fff",
                  padding: "0 8px",
                }}
              >
                Page Break
              </span>
            </div>
          );

        default:
          return null;
      }
    }

    if (section.type === "editable") {
      const isEmpty =
        !values[section.key] ||
        values[section.key].replace(/<[^>]*>/g, "").trim() === "";

      if (isEmpty) return null;

      return (
        <div key={section.key} className="mb-3">
          {section.label_key && (
            <p className="mb-1 fs-10">
              <strong>{t(section.label_key)}:</strong>
              <br />
            </p>
          )}

          <div className="commonStyles fs-10">
            <div
              id={section.key}
              dangerouslySetInnerHTML={{
                __html: values[section.key],
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  // Function to draw signature from state
  const drawSignature = useCallback((signature, signatureRef) => {
    if (signature && signatureRef.current) {
      const canvas = signatureRef.current;
      canvas.clear();
      canvas.fromDataURL(`data:image/png;base64,${signature}`);
    }
  }, []);

  // Handle window resize and redraw signature
  useEffect(() => {
    const handleResize = () => {
      drawSignature(clientSignature, signatureRefClient);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clientSignature, drawSignature]);

  const loadAndDrawSignature = (signatureData, signatureRef) => {
    if (!signatureData) return;

    const canvas = signatureRef?.current;

    //  canvas not mounted yet
    if (!canvas || typeof canvas.fromDataURL !== "function") {
      return;
    }

    canvas.fromDataURL(`data:image/png;base64,${signatureData}`);
  };

  if (!isImportedProposal && (!templateConfig || !Object.keys(sectionMap).length)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const renderLine = (text) => {
    if (!text) return null;

    let cleaned = String(text).trim();

    // remove leading hyphens like "-- ", "--- ", etc.
    cleaned = cleaned.replace(/^[-\s]+/, "");

    // remove placeholders or empty lines
    if (!cleaned) return null;

    return (
      <>
        {cleaned}
        <br />
      </>
    );
  };

  const getPagesFromSections = () => {
    const pages = [];
    let current = [];

    templateConfig.sections.forEach((sectionKey) => {
      if (sectionKey === "pageBreakAfterExtraContent") {
        pages.push([...current]);
        current = [];
      } else {
        current.push(sectionKey);
      }
    });

    if (current.length > 0) {
      pages.push(current);
    }

    return pages;
  };

  // Save the signature when it's drawn on the canvas
  const saveClientSignature = () => {
    if (signatureRefClient.current && !signatureRefClient.current.isEmpty()) {
      const signatureData = signatureRefClient.current
        .getTrimmedCanvas()
        .toDataURL("image/png");

      const base64Signature = signatureData.replace(
        /^data:image\/png;base64,/,
        ""
      );
      setClientSignature(base64Signature);
      setIsClientSignatureBtnDisabled(true); // Disable button after saving
    } else {
      toast.error(t("pleaseProvideClientSignature"));
    }
  };

  const clearClientSignature = () => {
    signatureRefClient.current.clear();
    setClientSignature("");
    setIsClientSignatureBtnDisabled(false); // Enabled button after clear
  };

  // Function to save the client signature and date using the API
  const saveClientSignatureData = async () => {
    if (!clientSignature) {
      toast.error(t("signatureIsRequired"));
      return;
    }

    try {
      const clientSignatureData = {
        ClientSignature: clientSignature,
        ClientSignatureDate: new Date().toISOString(),
      };

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/client-signature/${proposalDocumentDetail.ProposalDetailId}`,
        clientSignatureData, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      toast.success(t("signatureSaved"));
    } catch (error) {
      console.error("Signature Save Error:", error);
    }
  };

  // Function to update proposal status
  const updateProposalStatus = async (status, comment) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/proposalRoute/updateProposal-status/${proposalDocumentDetail.ProposalId}`,
        { Status: status, Comment: comment, Culture: localStorage.getItem("culture") || "nl" },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
    } catch (error) {
      console.error("Proposal Status Error:", error);
    }
  };

  // Function to update proposal details status
  const updateProposalDetailsStatus = async (detailsStatus) => {
    try {
      const proposalDetailsStatus = {
        Status: detailsStatus,
      };
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/update-status/${proposalDocumentDetail.ProposalDetailId}`,
        proposalDetailsStatus, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
    } catch (error) {
      console.error("Proposal Details Status Error:", error);
    }
  };

  const sendApprovedorRejectedEmail = async (status) => {
    const proposalValidationPath = `/validate-proposal/${formData.proposalDetailsData.ProposalDetailId}`;

    try {
      const domain = process.env.REACT_APP_DEFAULT_DOMAIN;
      const customerId = formData?.proposal?.CustomerId
      await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/sendProposalApprovalEmail`,
        {
          to: relationData.Emailaddress1,
          contactperson: relationData.Contactperson,
          locationAddress: `${formData.locationData.StreetName} ${formData.locationData.HouseNumber}`,
          locationTown: `${formData.locationData.PostCode} ${formData.locationData.City}`,
          proposalType: formData?.proposal?.TemplateName,
          documentLink: proposalValidationPath,
          culture: localStorage.getItem("culture") || "nl",
          communicationUserId: proposalDocumentDetail.CommunicationUserId,
          customerId,
          relationId: relationData.RelationId,
          status,
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      const successMessage =
        status === "approved" ? t("approvalEmailSent") : t("declineEmailSent");
      toast.success(successMessage);
    } catch (error) {
      console.error("Error sending proposal email:", error);
      const errorMessage =
        status === "approved"
          ? t("errorSendingApprovalEmail")
          : t("errorSendingDeclineEmail");
      toast.error(errorMessage);
    }
  };

  const handleAutoAsperionRelationUpdate = async () => {
    try {
      const locationId = formData?.proposal?.LocationId;
      const relationId = formData?.relationData?.RelationId;
      const relationTypeRaw = formData?.relationData?.Type;

      // Always normalize relationType to string (since model uses STRING)
      const relationType = relationTypeRaw?.toString();

      if (!locationId || !relationId) {
        return;
      }

      const { isAutoSyncEnabled } =
        locationConfig || (await getAsperionLocationConfig(locationId));

      if (!isAutoSyncEnabled) {
        return;
      }

      // Only update if relation is Prospect (1)
      if (relationType !== "1") {
        return;
      }

      // Only send the two fields this endpoint is responsible for
      const payload = {
        type: "0",           // convert Prospect → Client
        asperionSource: "1", // mark as synced
      };

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/relationRoute/type-source/${relationId}`,
        payload,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

    } catch (error) {
      console.error("Auto Asperion Relation Update Error:", error?.response?.data || error.message);
    }
  };

  // --- Helper: refresh Asperion access token ---
  const refreshAsperionToken = async (currentRefreshToken, currentAsperionUserId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/asperionAutoTokenRoute/refreshToken`,
        { asperionUserId: currentAsperionUserId, refreshToken: currentRefreshToken },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      const tokens = response.data;
      setAccessToken(tokens.AccessToken);
      setRefreshToken(tokens.RefreshToken);
      return tokens;
    } catch (error) {
      console.error("Failed to refresh Asperion token:", error);
      throw error;
    }
  };

  // --- Helper: fetch location and check auto-sync ---
  const getAsperionLocationConfig = async (locationId) => {
    const locationResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/locationRoute/getById/${locationId}`,
      { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    );
    const locationData = locationResponse?.data?.data || locationResponse?.data;
    const autoSyncFlag = locationData?.AutoAsperionSyncFlag;
    const isAutoSyncEnabled = autoSyncFlag === true || autoSyncFlag === 1 || autoSyncFlag === "1";
    return { locationData, isAutoSyncEnabled };
  };

  // --- Helper: call Asperion API with auto token refresh on 401 ---
  // Mirrors exact try/catch pattern from reference method
  const callWithTokenRefresh = async (apiCall, currentAccessToken, currentRefreshToken) => {
    try {
      const result = await apiCall(currentAccessToken);
      return { result, accessToken: currentAccessToken, refreshToken: currentRefreshToken };
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await refreshAsperionToken(currentRefreshToken, asperionUserId);
        const newAccessToken = refreshed.AccessToken;
        const newRefreshToken = refreshed.RefreshToken;
        const result = await apiCall(newAccessToken);
        return { result, accessToken: newAccessToken, refreshToken: newRefreshToken };
      }
      throw err;
    }
  };

  // --- Helper: create debtor in Asperion and update relation ---
  const createAndSyncDebtor = async (currentAccessToken, currentRefreshToken, tenantId, data, relationId, paymentConditionId) => {
    const asperionData = {
      cocNumber: data.CocNumber || null,
      directDebit: true,
      iban: data.Iban || null,
      vatNumber: data.VatNumber || null,
      address: data.StreetName,
      postCode: data.PostCode,
      countryCode: "NL",
      paymentConditionId,
      phoneNumber: data.PhoneNumber || "",
      mobileNumber: data.MobileNumber || "",
      emailAddress: data.Emailaddress1,
      name: data.Name,
      city: data.City,
    };

    const { result: createResponse } = await callWithTokenRefresh(
      (token) =>
        axios.post(
          `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/post`,
          asperionData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "x-tenant-id": tenantId,
            },
          }
        ),
      currentAccessToken,
      currentRefreshToken
    );

    if (createResponse.status === 200) {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/relationRoute/updateRelationByAsperiondata/${relationId}`,
        {
          asperionRelationId: createResponse.data.id,
          asperionRelationCustomerNumber: createResponse.data.customerNumber,
        },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
    }
  };

  // --- Auto-create debtor in Asperion when prospect is approved as client ---
  const createDebtorInAsperionIfNeeded = async () => {
    try {
      const locationId = formData?.proposal?.LocationId;
      const relationType = formData?.relationData?.Type?.toString();

      // Only run for prospects being converted to clients
      if (relationType !== "1") return;
      if (!accessToken) return;

      const { locationData, isAutoSyncEnabled } =
        locationConfig || (await getAsperionLocationConfig(locationId));
      if (!isAutoSyncEnabled) return;

      const tenantId = locationData?.AsperionTenantId;
      const relationId = formData?.relationData?.RelationId;
      const data = formData?.relationData;

      let currentAccessToken = accessToken;
      let currentRefreshToken = refreshToken;

      // Fetch payment conditions
      const { result: pcResponse, accessToken: atAfterPc, refreshToken: rtAfterPc } =
        await callWithTokenRefresh(
          (token) => axios.get(
            `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/fetchpaymentConditions`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "x-tenant-id": tenantId,
              },
            }
          ),
          currentAccessToken,
          currentRefreshToken
        );
      currentAccessToken = atAfterPc;
      currentRefreshToken = rtAfterPc;
      const paymentConditionId = pcResponse.data._data[0].id;

      // Fetch existing debtors to check for duplicate
      const { result: debtorsResponse, accessToken: atAfterDebtors, refreshToken: rtAfterDebtors } =
        await callWithTokenRefresh(
          (token) => axios.get(
            `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/fetch`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "x-tenant-id": tenantId,
              },
            }
          ),
          currentAccessToken,
          currentRefreshToken
        );
      currentAccessToken = atAfterDebtors;
      currentRefreshToken = rtAfterDebtors;

      const debtors = debtorsResponse.data._data;
      const asperionRelationId = data?.AsperionRelationId;
      const alreadyExists = debtors.some(
        (debtor) => String(asperionRelationId) === String(debtor.id)
      );

      // Already synced to Asperion, skip creation
      if (alreadyExists) return;

      await createAndSyncDebtor(
        currentAccessToken,
        currentRefreshToken,
        tenantId,
        data,
        relationId,
        paymentConditionId
      );
    } catch (error) {
      // Non-blocking: log but don't prevent approval from completing
      console.error(
        "Auto Asperion debtor creation error:",
        error?.response?.data || error.message
      );
    }
  };

  const uploadAndReplaceProposalPdf = async () => {
    const signedBlobUrl = await handleUploadPDF(
      invoiceRef,
      {
        ...formData,
        customerId: formData?.proposal?.CustomerId,
        workType: formData?.proposal?.WorkType,
        selectedClientData: {
          RelationId: formData?.relationData?.RelationId,
        },
      },
      formData?.proposal?.CurrentVersionId,
      () => { }
    );

    if (!signedBlobUrl) {
      throw new Error("PDF upload failed — aborting approval");
    }

    const existingDocument = formData?.proposalDetailsData?.Document;
    if (existingDocument) {
      axios
        .delete(`${process.env.REACT_APP_API_URL}/blobRoute/deleteBlob`, {
          params: { blobUrl: existingDocument },
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
        })
        .catch((deleteErr) => console.warn("Could not delete existing PDF blob:", deleteErr));
    }

    await axios.post(
      `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/addUpdateProposalDetails`,
      {
        proposalDetailId: proposalDocumentDetail.ProposalDetailId,
        document: signedBlobUrl,
      },
      {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret,
        },
      }
    );
  };

  // -------------------------------------------------------------

  const handleApproveClick = async () => {
    if (
      !clientSignature ||
      (signatureRefClient.current && signatureRefClient.current.isEmpty())
    ) {
      toast.error(t("errorSendingApprovalEmail"));
      return;
    }

    setIsLoading(true);

    try {
      await saveClientSignatureData();
      await uploadAndReplaceProposalPdf();

      await Promise.all([
        updateProposalStatus("1", comment),
        updateProposalDetailsStatus("1"),
      ]);

      await handleAutoAsperionRelationUpdate();
      await createDebtorInAsperionIfNeeded(); // Auto-create debtor in Asperion if enabled

      sendApprovedorRejectedEmail("approved").catch((err) =>
        console.error("Approval email failed to send:", err)
      );

      toast.success(t("proposalApproved"));
      setJustApproved(true);
    } catch (error) {
      toast.error(t("errorWhileApproving"));
      console.error("Approval Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectClick = async () => {
    setIsLoading(true);
    try {
      await updateProposalStatus("2", comment);
      await updateProposalDetailsStatus("2");

      sendApprovedorRejectedEmail("rejected").catch((err) =>
        console.error("Decline email failed to send:", err)
      );

      toast.success(t("proposalDeclined"));
    } catch (error) {
      toast.error(t("errorWhileDeclining"));
      console.error("Reject Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;

    const original = invoiceRef.current;
    const clone = original.cloneNode(true);

    const alignFix = document.createElement("style");
    alignFix.innerHTML = `.client-sig-col { margin-top: 0 !important; }`;
    clone.appendChild(alignFix);

    clone.style.width = "794px";
    clone.style.maxWidth = "none";
    clone.style.minWidth = "794px";
    clone.style.background = "#ffffff";
    clone.style.overflow = "visible";
    clone.style.boxSizing = "border-box";
    clone.style.position = "fixed";
    clone.style.top = "-9999px";
    clone.style.left = "-9999px";
    clone.style.transform = "none";

    const clonedImgs = clone.querySelectorAll("img");
    for (const img of clonedImgs) {
      const isSignature =
        img.style.width === "150px" && img.style.height === "60px";

      if (isSignature) {
        try {
          const fitted = await fitImageToBox(img.src, 150, 60);
          img.src = fitted;
        } catch (err) {
          console.warn("Signature fit failed, falling back to CSS scaling:", err);
        }
        img.style.setProperty("width", "150px", "important");
        img.style.setProperty("height", "60px", "important");
        img.style.setProperty("display", "block", "important");
      }
    }

    document.body.appendChild(clone);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const padding = 10;
      let yPosition = padding;
      let contentDrawn = false;

      const children = Array.from(clone.children);

      for (const child of children) {
        if (!child || child.offsetHeight === 0) continue;

        const canvas = await html2canvas(child, {
          scale: 1.7,
          useCORS: true,
          windowWidth: 794,
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = pageWidth - padding * 2;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (contentDrawn && yPosition + imgHeight > pageHeight - padding) {
          pdf.addPage();
          yPosition = padding;
        }

        pdf.addImage(imgData, "JPEG", padding, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight;
        contentDrawn = true;
      }

      pdf.save(`Proposal_${formData.relationData.Name}.pdf`);
      toast.success(t("pdfDownloadSuccess"));
    } catch (err) {
      console.error("PDF Download Error:", err);
      toast.error(t("pdfDownloadError"));
    } finally {

      document.body.removeChild(clone);
    }
  };

  const downloadImportedPdf = () => {
    try {
      if (!importedPdfBlob) {
        toast.error(t("pdfDownloadError"));
        return;
      }
      const url = URL.createObjectURL(importedPdfBlob);
      const link = document.createElement("a");

      link.href = url;
      const proposalLabel = t("proposal");
      const clientName = formData?.relationData?.Name;

      link.download = `${proposalLabel}_${clientName}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success(t("pdfDownloadSuccess"));
    } catch (err) {
      console.error("Cached PDF download error:", err);
      toast.error(t("pdfDownloadError"));
    }
  };

  const handleClientSignatureSave = (base64) => {
    setTempClientSignature(base64);
    setClientSignature(base64);
    setIsClientSignatureBtnDisabled(true);
  };

  return (
    <>
      {isImportedProposal && (
        <div style={{ position: "relative" }}>

          {pdfLoading && (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          {pdfUrl && !pdfLoading && (
            <PDFVierwer
              pdfUrl={pdfUrl}
              onRendered={() => setPdfLoading(false)}
            />
          )}
        </div>
      )}

      {/* loader */}
      {showGlobalLoader && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255, 255, 255, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">{t("savingProposalPleaseWait")}</p>
          </div>
        </div>
      )}

      {/* rendered template */}
      {!isImportedProposal && (
        <div ref={invoiceRef}>
          {getPagesFromSections().map((sectionKeys, pageIndex, allPages) => (
            <Card key={pageIndex} className="mb-3" style={{ marginTop: 0 }}>
              <Card.Body>
                {/* First page: render header */}
                {pageIndex === 0 && (
                  <>
                    <Row className="align-items-center text-center mb-3">
                      <Col sm={6} className="text-sm-start">
                        {logoSrc && (
                          <img
                            src={logoSrc}
                            alt="invoice"
                            width={150}
                            style={{
                              border: "none",
                              boxShadow: "none",
                              background: "transparent",
                            }}
                          />
                        )}
                      </Col>
                      <Col className="text-sm-end mt-3 mt-sm-0">
                        {/* Address block */}
                        <p className="fs-10 mb-0">
                          {renderLine(
                            [
                              formData?.settings?.OrganizationName
                            ]
                              .filter(Boolean)
                              .join(" ")
                          )}
                          {renderLine(
                            [
                              formData?.locationData?.StreetName,
                              formData?.locationData?.HouseNumber,
                            ]
                              .filter(Boolean)
                              .join(" ")
                          )}
                          {renderLine(
                            [
                              formData?.locationData?.PostCode,
                              formData?.locationData?.City,
                            ]
                              .filter(Boolean)
                              .join(" ")
                          )}
                        </p>
                        <p className="fs-10">
                          {renderLine(formData?.userData?.PhoneNumber)}
                          {renderLine(formData?.userData?.Emailaddress)}
                          {formData?.settings?.DomainName !== "app.getplan.nl" &&
                            renderLine(`www.${formData?.settings?.DomainName}`)}
                        </p>
                      </Col>
                    </Row>

                    <Row className="align-items-end">
                      <Col xs={12} sm={8} className="text-sm-start mb-3 mb-sm-0">
                        <p className="fs-10 mb-0">
                          {renderLine(formData?.relationData?.Name)}
                          {renderLine(getTranslatedSalutation(formData?.relationData, t))}
                          {renderLine(
                            [
                              formData?.relationData?.StreetName,
                              formData?.relationData?.PostCode,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          )}
                          {renderLine(
                            [
                              formData?.relationData?.City,
                              formData?.relationData?.Country,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          )}
                          {renderLine(formData?.relationData?.Emailaddress1)}
                        </p>
                      </Col>
                      <Col xs={12} sm={4} className="text-sm-end mb-3 mb-sm-0">
                        <p className="fs-10 mb-0">
                          {t("date")},{" "}
                          {formData?.proposalDetailsData.Date
                            ? moment(formData.proposalDetailsData.Date).format(
                              "DD-MM-YYYY"
                            )
                            : moment().format("DD-MM-YYYY")}
                        </p>
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <p className="fs-10 mb-2">
                        {formData?.relationData?.Salutation || formData?.relationData?.FirstName
                          ? t("dear")
                          : t("dearSirMadam")}{" "}
                        {getTranslatedSalutation(formData?.relationData, t)},
                      </p>
                    </div>
                  </>
                )}

                {/* Page content */}
                {sectionKeys.map(renderSection)}

                {/* Footer: only on last page */}
                {pageIndex === allPages.length - 1 && (
                  <>
                    <p className="fs-10 mb-2">{t("signedCopyReturn")}</p>
                    <p className="fs-10 mb-2">{t("greeting")}</p>

                    {/* Signature Block */}
                    <div className="mt-4 mb-2 fs-10">
                      <Row className="mt-3">
                        {/* Company Signature */}
                        <Col xs={12} md={6} className="text-left order-1 order-md-1">
                          {companySignatureImage ? (
                            <>
                              <img
                                src={companySignatureImage}
                                alt={t("companySignature")}
                                className="p-2 mt-2"
                                style={{
                                  width: "150px",
                                  height: "60px",
                                  objectFit: "contain",
                                }}
                              />
                              <p className="fs-10 mb-0">
                                {companySignatureDate && (
                                  <strong>
                                    {t("dateOfSigning")} {companySignatureDate}
                                  </strong>
                                )}
                              </p>

                              {/* Company Address */}
                              <p className="fs-10 mt-2 mb-0">
                                {formData?.userData.Name || "--"},
                                <br />
                                {formData?.settings?.OrganizationName || "--"}
                                <br />
                                {formData?.locationData?.City || "--"}
                              </p>
                            </>
                          ) : (
                            <>
                              <h6>{t("companySignature")}</h6>
                              <div
                                style={{
                                  paddingTop: "20px",
                                  color: "#666",
                                  fontStyle: "italic",
                                }}
                              >
                                <p className="mb-2">
                                  _______________________________
                                </p>
                                <p className="mb-2">
                                  <small>{t("noSignatureAvailable")}</small>
                                </p>
                              </div>

                              {/* Company Address */}
                              <p className="fs-10 mt-2 mb-0">
                                {formData?.userData.Name || "--"},
                                <br />
                                {formData?.settings?.OrganizationName || "--"}
                                <br />
                                {formData?.locationData?.City || "--"}
                              </p>
                            </>
                          )}
                        </Col>

                        {/* Client Signature */}
                        <Col xs={12} md={6} className="text-left order-2 order-md-2 mt-3 mt-md-0 client-sig-col">
                          {isApproved &&
                            (formData?.proposalDetailsData?.ClientSignature || clientSignature) ? (
                            <>
                              <img
                                src={`data:image/png;base64,${formData?.proposalDetailsData?.ClientSignature || clientSignature}`}
                                alt={t("customerSignature")}
                                className="p-2 mt-2"
                                style={{
                                  width: "150px",
                                  height: "60px",
                                  objectFit: "contain",
                                }}
                              />
                              <p className="fs-10 mb-0">
                                {formData?.proposalDetailsData?.ClientSignatureDate ? (
                                  <strong>
                                    {t("dateOfSigning")}{" "}
                                    {moment(
                                      formData?.proposalDetailsData
                                        ?.ClientSignatureDate
                                    ).format("DD-MM-YYYY")}
                                  </strong>
                                ) : (
                                  <strong>
                                    {t("dateOfSigning")}{" "}
                                    {moment().format("DD-MM-YYYY")}
                                  </strong>
                                )}
                              </p>

                              {/* Client Address */}
                              <p className="fs-10 mt-2 mb-0">
                                {getTranslatedSalutation(formData?.relationData, t)}
                                <br />
                                {formData?.relationData?.Name || ""}
                                <br />
                                {formData?.relationData?.City || ""}
                              </p>
                            </>
                          ) : (
                            <>
                              <div
                                className="sig-canvas-wrapper sig-canvas-wrapper--small signature-box-fixed position-relative d-flex align-items-center justify-content-center"
                                onClick={() => setShowSignatureModal(true)}
                              >
                                {tempClientSignature ? (
                                  <img
                                    src={`data:image/png;base64,${tempClientSignature}`}
                                    alt="Client signature"
                                    className="signature-preview-img"
                                  />
                                ) : (
                                  <span className="text-muted">
                                    {t("clickToSign")}
                                  </span>
                                )}

                                {tempClientSignature && (
                                  <div
                                    className="position-absolute top-0 end-0 m-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTempClientSignature(null);
                                      setClientSignature("");
                                      setIsClientSignatureBtnDisabled(false);
                                    }}
                                    title={t("clear")}
                                  >
                                    <button className="btn btn-light sig-icon-btn rounded-circle p-0 shadow-sm">
                                      <i className="fa fa-broom"></i>
                                    </button>
                                  </div>
                                )}
                              </div>
                              <p className="fs-10 mb-0 mt-2">
                                {formData?.proposalDetailsData
                                  ?.ClientSignatureDate ? (
                                  <strong>
                                    {t("dateOfSigning")}{" "}
                                    {moment(
                                      formData?.proposalDetailsData
                                        ?.ClientSignatureDate
                                    ).format("DD-MM-YYYY")}
                                  </strong>
                                ) : clientSignature ? (
                                  <strong>
                                    {t("dateOfSigning")}{" "}
                                    {moment().format("DD-MM-YYYY")}
                                  </strong>
                                ) : null}
                              </p>

                              {/* Client Address */}
                              <p className="fs-10 mt-2 mb-0">
                                {formData?.relationData?.Contactperson || ""}
                                <br />
                                {formData?.relationData?.Name || ""}
                                <br />
                                {formData?.relationData?.City || ""}
                              </p>
                            </>
                          )}
                        </Col>
                      </Row>
                    </div>

                    {/* Footer details */}
                    <div
                      className="fs-10 text-end mt-5 text-muted"
                      style={{ fontSize: "12px", opacity: 0.7 }}
                    >
                      <p className="mb-0">
                        {/* NL footer */}
                        {!formData?.finLabels?.showIfsc && (
                          <>
                            <strong>{t("iban")}:</strong> {formData?.locationData?.Iban ?? "--"}
                            <br />
                            <strong>{t("gAccount")}:</strong> {formData?.locationData?.GAccount ?? "--"}
                            <br />
                            <strong>{t("chamberOfCommerce")}:</strong> {formData?.locationData?.KvKDetails ?? "--"}
                            <br />
                            <strong>{t("vatNumber")}:</strong> {formData?.locationData?.BTWDetails ?? "--"}
                          </>
                        )}
                        {formData?.finLabels?.showIfsc && (
                          <>
                            <strong>{t(formData?.finLabels?.account)}:</strong> {formData?.locationData?.AccountNumber ?? "--"}
                            <br />
                            <strong>{t(formData?.finLabels?.ifsc)}:</strong> {formData?.locationData?.IfscCode ?? "--"}
                            <br />
                            <strong>{t(formData?.finLabels?.gst)}:</strong> {formData?.locationData?.GstDetails ?? "--"}
                          </>
                        )}

                      </p>
                    </div>

                    <Row className="mt-4 mb-2">
                      <Col>
                        <Form.Group controlId="opmerking">
                          <Form.Label>{t("commentLabel")}</Form.Label>
                          {isApproved ? (
                            <p className="form-control-plaintext fs-10">
                              {comment || "-"}
                            </p>
                          ) : (
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              placeholder={t("commentPlaceholder")}
                            />
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <hr className="m-0"></hr>

      {/* Action Buttons */}
      <Card className="mt-2 no-shadow">
        <Card.Body className="p-2">
          <Row className="justify-content-end mb-4">
            <Col xs="auto" className="d-flex flex-column align-items-end gap-2">
              {!isApproved ? (
                <>
                  <p className="mb-0 text-danger">
                    <small>*{t("confirmationOfAcceptance")}</small>
                  </p>
                  <div>
                    <IconButton
                      size="sm"
                      icon="times"
                      className="me-1"
                      iconClassName="me-1"
                      onClick={handleRejectClick}
                      disabled={isLoading || formData.isLoading}
                      variant="danger"
                    >
                      {t("decline")}
                    </IconButton>

                    <IconButton
                      size="sm"
                      icon="check"
                      iconClassName="me-1"
                      onClick={handleApproveClick}
                      disabled={isLoading || !clientSignature}
                      variant="primary"
                    >
                      {t("accept")}
                    </IconButton>
                  </div>
                </>
              ) : (
                <IconButton
                  size="sm"
                  icon="download"
                  iconClassName="me-1"
                  onClick={isImportedProposal ? downloadImportedPdf : handleDownloadPDF}
                  disabled={isDownloadDisabled}
                >
                  {t("download")}
                </IconButton>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <SignatureModal
        show={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        setCompanySignature={handleClientSignatureSave}
        userData={{}}
        mode="client"
      />
    </>
  );
};

export default TemplateViewerClient;